import React, {FC, useState, useMemo, useEffect, useRef} from "react";
import {useApolloClient} from "@apollo/client";
import {Button, Tooltip, Space, Progress, Spin, Result, Alert} from "antd";
import {CSVLink} from "react-csv";
import {LinkProps} from "react-csv/components/Link";
import moment from "moment";
import {DownloadOutlined, SettingOutlined} from "@ant-design/icons";
import {blue} from "@ant-design/colors";
import {colorBackgroundBase} from "../../../styles/colors";
import {ExportPaymentsModalProps, ExportConfig, DateRange} from "./types";
import DateRangeSelector from "./components/DateRangeSelector";
import {useExportPaymentsLogic} from "./hooks/useExportPaymentsLogic";
import {getDateRangeFromPreset} from "./utils/dateRangePresets";
import {PAYMENT_EXPORT_CONFIG} from "./utils/exportConfig";
import {useCurrency} from "../../../helpers/currencyHelper";
import {TablesEnum, useGetColumnsForTableQuery, GetColumnsForTableDocument, useUpdateTableColumnSettingsMutation} from "../../../generated/graphql";
import {getCSVData} from "../../../helpers/tableCsvExport/getCSVData";
import ColumnConfigurator from "../../../views/ColumnConfigurator/ColumnConfigurator";
import ActivateAllColumnsButton from "../../../views/ActivateAllColumnsButton/ActivateAllColumnsButton";
import DraggableModal from "../../../views/DraggableModal/DraggableModal";
import {GET_PAID_PAYMENTS} from "../scheduledPayments.graphql";

// Die react-csv-Typen liefern eine Klassen-Instanz, die @types/react 17 nicht als JSX-Element akzeptiert.
const CsvLink = CSVLink as unknown as FC<LinkProps>;

export const DEFAULT_PAYMENT_EXPORT_CONFIG: ExportConfig = {
    title: "Umsätze exportieren",
    entityLabel: "Umsätze",
    dateFieldKey: "paymentDone",
    dateFieldTable: "scheduledPayment",
    dateFieldDescription: "Zeitraum bezieht sich auf: Bezahldatum",
    fileNamePrefix: "Umsaetze-Export",
    csvTablesEnum: TablesEnum.DonePaymentsList,
    exportTablesEnum: TablesEnum.DonePaymentsListExport,
    query: GET_PAID_PAYMENTS,
    getItemsFromResult: (data: any) => ({
        items: data.paidScheduledPayments?.items ?? [],
        total: data.paidScheduledPayments?.total ?? 0,
    }),
    buildVariables: (options: any) => ({options, bookingId: null}),
    sortField: "paymentDone",
};

const ExportPaymentsModal: FC<ExportPaymentsModalProps> = ({options, columnsToShow: defaultColumnsToShow, allColumns, hiddenColumns, mandatoryColumns, exportConfig: externalConfig}) => {
    const config = externalConfig || DEFAULT_PAYMENT_EXPORT_CONFIG;
    const apolloClient = useApolloClient();

    const [open, setOpen] = useState(false);
    const [dateRange, setDateRange] = useState<DateRange>(getDateRangeFromPreset("currentMonth"));
    const [respectFilters, setRespectFilters] = useState(false);
    const [estimatedCount, setEstimatedCount] = useState<number>(0);
    const [columnSelectorVisible, setColumnSelectorVisible] = useState(false);
    const [columnsToShow, setColumnsToShow] = useState<Array<string>>(defaultColumnsToShow || []);
    const [countLoading, setCountLoading] = useState(false);
    // Nur die Antwort der jüngsten Zähl-Abfrage zählt: Beim Öffnen und bei Zeitraumwechseln laufen
    // mehrere Abfragen parallel, eine spät eintreffende ältere setzte sonst den Zähler eines anderen
    // Zeitraums (537 statt 98) und der Export rechnete mit falschen Chunks.
    const countRequestId = useRef(0);
    const currency = useCurrency();

    const exportTableEnum = config.exportTablesEnum;

    // Lade User-Spalten-Einstellungen für Export
    const {data: userColumnsData, refetch: refetchUserColumns} = useGetColumnsForTableQuery({
        skip: !open,
        variables: {table: exportTableEnum},
    });

    const userColumns = userColumnsData?.getColumnsForTable;

    // Setze User-Spalten wenn verfügbar, sonst Default-Spalten
    useEffect(() => {
        if (userColumns && userColumns.length > 0) {
            setColumnsToShow(userColumns);
        } else if (defaultColumnsToShow && defaultColumnsToShow.length > 0) {
            setColumnsToShow(defaultColumnsToShow);
        }
    }, [userColumns, defaultColumnsToShow]);

    // Funktion zum Zurücksetzen auf Default-Spalten
    const [updateUserColumnsMutation] = useUpdateTableColumnSettingsMutation();
    const restoreDefaultColumns = () => {
        if (defaultColumnsToShow && defaultColumnsToShow.length > 0) {
            try {
                updateUserColumnsMutation({
                    variables: {
                        updateTableColumnSettingsInput: {
                            table: exportTableEnum,
                            columns: defaultColumnsToShow,
                        },
                    },
                    refetchQueries: [
                        "GetColumnsForTable",
                        {
                            query: GetColumnsForTableDocument,
                            variables: {table: exportTableEnum},
                        },
                    ],
                });
            } catch (error) {
                console.log("error", error);
            } finally {
                refetchUserColumns({table: exportTableEnum}).then(() => {
                    setColumnsToShow(defaultColumnsToShow);
                });
            }
        }
    };

    const exportLogic = useExportPaymentsLogic({
        options,
        dateRange,
        respectFilters,
        exportConfig: config,
    });

    // Query zum Zählen der Einträge im gewählten Zeitraum
    const createCountOptions = () => {
        const baseFilters = respectFilters && options && options.filter ? options.filter.filter((f) => f.column !== config.dateFieldKey) : [];

        const dateFilter = {
            column: config.dateFieldKey,
            // Obergrenze = lokales TagesENDE (siehe useExportPaymentsLogic): muss exakt der Export-Serialisierung
            // entsprechen, sonst weicht der "X Einträge"-Zähler vom tatsächlichen CSV-Export ab.
            values: [dateRange.start.clone().startOf("day").toISOString(), dateRange.end.clone().endOf("day").toISOString()],
            table: config.dateFieldTable,
        };

        return {
            filter: [...baseFilters, dateFilter],
            limit: 1,
            offset: 0,
        };
    };

    // Count-Query via apolloClient.query() statt hardcoded Hook
    const fetchCount = () => {
        if (!open) return;
        setCountLoading(true);
        const requestId = ++countRequestId.current;
        apolloClient
            .query({
                query: config.query,
                variables: config.buildVariables(createCountOptions()),
                fetchPolicy: "network-only",
            })
            .then((result) => {
                if (requestId !== countRequestId.current) return;
                if (result && result.data) {
                    const extracted = config.getItemsFromResult(result.data);
                    setEstimatedCount(extracted.total || 0);
                    exportLogic.setTotalCount(extracted.total || 0);
                }
            })
            .catch(() => {
                if (requestId === countRequestId.current) setEstimatedCount(0);
            })
            .finally(() => {
                if (requestId === countRequestId.current) setCountLoading(false);
            });
    };

    useEffect(() => {
        if (open) {
            fetchCount();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateRange, respectFilters, open]);

    const handleDateRangeChange = (newDateRange: DateRange) => {
        setDateRange(newDateRange);
    };

    const handleRespectFiltersChange = (respect: boolean) => {
        setRespectFilters(respect);
    };

    const startExport = () => {
        exportLogic.handleExport(estimatedCount);
    };

    const handleClose = () => {
        exportLogic.resetExport();
        // Der DateRangeSelector startet beim nächsten Öffnen wieder mit „Aktueller Monat“;
        // ohne Reset exportierte der Dialog diesen Monat, zählte aber noch den alten Zeitraum.
        setDateRange(getDateRangeFromPreset("currentMonth"));
        // Eine noch laufende Zähl-Abfrage des alten Zeitraums darf den Zähler nicht mehr setzen.
        countRequestId.current++;
        setEstimatedCount(0);
        setCountLoading(false);
        setOpen(false);
    };

    // CSV-Daten konvertieren
    const csvData = useMemo(() => {
        if (!exportLogic.exportData || exportLogic.exportData.length === 0) {
            return [];
        }

        const sortField = config.sortField || config.dateFieldKey;

        // Sortiere die Daten clientseitig nach dem konfigurierten Feld
        let sortedData = [...exportLogic.exportData];
        if (!respectFilters) {
            sortedData.sort((a, b) => {
                const dateA = a[sortField] ? new Date(a[sortField] as string).getTime() : 0;
                const dateB = b[sortField] ? new Date(b[sortField] as string).getTime() : 0;
                return dateA - dateB;
            });
        }

        const visibleColumns = columnsToShow || Object.keys(allColumns);

        return getCSVData({
            selectedRows: sortedData,
            userColumns: visibleColumns,
            allColumns,
            hiddenColumns,
            currency,
            tablesEnum: config.csvTablesEnum,
            outputFormat: "csv",
        });
    }, [exportLogic.exportData, columnsToShow, allColumns, currency, hiddenColumns, respectFilters, config]);

    const renderSpinSection = (loading: boolean) => {
        return (
            <div style={{width: "100%"}}>
                <div
                    style={{
                        height: 50,
                        display: "flex",
                        justifyContent: "center",
                    }}
                >
                    <Spin spinning={loading} />
                </div>
                <Button
                    danger
                    onClick={() => {
                        exportLogic.setIsExporting(false);
                        exportLogic.resetExport();
                    }}
                    block
                >
                    {"Export abbrechen"}
                </Button>
            </div>
        );
    };

    const renderProgressSection = () => {
        const loadedCount = exportLogic.exportData.length;
        const total = exportLogic.totalCount;

        return (
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    marginTop: 20,
                }}
            >
                <Progress percent={exportLogic.progress} status={exportLogic.isExporting ? "active" : "normal"} format={() => `${loadedCount} / ${total}`} style={{width: "90%"}} />
            </div>
        );
    };

    const renderContent = () => {
        if (exportLogic.exportState === "error") {
            return <Alert message={`Fehler beim Exportieren der ${config.entityLabel}`} description="Bitte versuchen Sie es erneut oder wählen Sie einen kleineren Zeitraum." type="error" />;
        }

        if (exportLogic.exportState === "success") {
            return <Result status="success" title="Export erfolgreich" subTitle={`${exportLogic.exportData.length} ${config.entityLabel} wurden erfolgreich exportiert und können jetzt heruntergeladen werden.`} />;
        }

        if (exportLogic.isExporting) {
            return (
                <div>
                    {renderSpinSection(exportLogic.exportLoading)}
                    {renderProgressSection()}
                </div>
            );
        }

        return (
            <div>
                <DateRangeSelector onDateRangeChange={handleDateRangeChange} onRespectFiltersChange={handleRespectFiltersChange} respectFilters={respectFilters} dateFieldDescription={config.dateFieldDescription} entityLabel={config.entityLabel} />

                <div style={{marginTop: 15, marginBottom: 5, border: `1px solid ${blue[2]}`, backgroundColor: colorBackgroundBase, borderRadius: 4, padding: 0}}>
                    <div style={{padding: 3, paddingBottom: 0}}>
                        <Space style={{width: "100%", justifyContent: "space-between"}}>
                            <Button size="small" type="link" icon={<SettingOutlined />} onClick={() => setColumnSelectorVisible(!columnSelectorVisible)}>
                                {`Spalten konfigurieren (${columnsToShow.length})`}
                            </Button>
                            {columnSelectorVisible && (
                                <Space size="small">
                                    <Button type="link" size="small" onClick={restoreDefaultColumns}>
                                        {"Standard wiederherstellen"}
                                    </Button>
                                    <ActivateAllColumnsButton setSelectedColumns={setColumnsToShow} allColumns={allColumns} tablesEnum={exportTableEnum} />
                                </Space>
                            )}
                        </Space>
                    </div>

                    {columnSelectorVisible && (
                        <div style={{marginBottom: 15, maxHeight: 200, overflowY: "auto", padding: 10, paddingBottom: 0}}>
                            <ColumnConfigurator
                                columnsToShow={columnsToShow}
                                allColumns={allColumns}
                                setSelectedColumns={setColumnsToShow}
                                setColumnSelectorVisible={setColumnSelectorVisible}
                                hiddenColumns={hiddenColumns || []}
                                mandatoryColumns={mandatoryColumns || []}
                                tablesEnum={exportTableEnum}
                            />
                        </div>
                    )}
                </div>

                <div style={{marginTop: 15, marginBottom: 10, textAlign: "center"}}>
                    <div style={{fontSize: 13, color: "#666"}}>{`Geschätzte Anzahl ${config.entityLabel}:`}</div>
                    <div style={{fontWeight: "bold", fontSize: 16}}>{countLoading ? <Spin size="small" /> : estimatedCount.toLocaleString("de-DE")}</div>
                </div>

                <Tooltip
                    title={
                        estimatedCount === 0
                            ? `Keine ${config.entityLabel} im ausgewählten Zeitraum gefunden`
                            : estimatedCount > PAYMENT_EXPORT_CONFIG.MAX_EXPORT_LIMIT
                            ? `Maximale Export-Limit von ${PAYMENT_EXPORT_CONFIG.MAX_EXPORT_LIMIT.toLocaleString(
                                  "de-DE",
                              )} ${config.entityLabel} überschritten. Bitte wählen Sie einen kleineren Zeitraum oder setzen Sie Filter.`
                            : undefined
                    }
                >
                    <Button onClick={startExport} type="primary" disabled={countLoading || estimatedCount === 0 || estimatedCount > PAYMENT_EXPORT_CONFIG.MAX_EXPORT_LIMIT} block>
                        {"Export starten"}
                    </Button>
                </Tooltip>

                {estimatedCount > PAYMENT_EXPORT_CONFIG.MAX_EXPORT_LIMIT && (
                    <Alert
                        style={{marginTop: 10}}
                        message="Zu viele Daten"
                        description={`Der Export ist auf maximal ${PAYMENT_EXPORT_CONFIG.MAX_EXPORT_LIMIT.toLocaleString(
                            "de-DE",
                        )} ${config.entityLabel} limitiert. Bitte wählen Sie einen kleineren Zeitraum oder verwenden Sie Filter.`}
                        type="warning"
                        showIcon
                    />
                )}
            </div>
        );
    };

    // Modal Footer
    const renderModalFooter = () => [
        <Button key="cancel" onClick={handleClose} disabled={exportLogic.isExporting}>
            {exportLogic.exportState === "success" ? "Schließen" : "Abbrechen"}
        </Button>,
        <CsvLink key="export" data={csvData} filename={exportLogic.exportFileName || `${config.fileNamePrefix}-${moment().format("YYYY-MM-DD")}.csv`} separator=";" target="_blank">
            <Button type="primary" disabled={exportLogic.exportState !== "success" || exportLogic.exportData.length === 0}>
                CSV herunterladen ({csvData.length} Einträge)
            </Button>
        </CsvLink>,
    ];

    if (!open) {
        return (
            <Tooltip title={`${config.entityLabel} für einen bestimmten Zeitraum als CSV exportieren`}>
                <Button size="small" type="link" style={{fontSize: 12}} onClick={() => setOpen(true)}>
                    <Space size={5}>
                        <DownloadOutlined />
                        {config.title}
                    </Space>
                </Button>
            </Tooltip>
        );
    }

    return (
        <DraggableModal
            data-testid="export-payments-modal"
            draggable={true}
            boundsPadding={500}
            open={open}
            width="80%"
            onCancel={handleClose}
            forceRender
            destroyOnClose
            title={config.title}
            footer={renderModalFooter()}
        >
            {renderContent()}
        </DraggableModal>
    );
};

export default ExportPaymentsModal;
